<template>
  <v-row>
    <v-col cols="12">
      <v-card elevation="2" class="mb-4">
        <v-toolbar color="secondary" elevation="1">
          <v-spacer />Alunos da turma<v-spacer />
        </v-toolbar>
        <v-card-text>
          <v-row class="mb-4" align="center">
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedTurmaId"
                :items="turmas"
                item-title="display"
                item-value="id"
                label="Turma"
                :loading="loadingTurmas"
                hide-details
              />
            </v-col>
          </v-row>
          <v-alert
            v-if="selectedTurmaId && !filteredStudentList.length"
            type="info"
            variant="tonal"
            class="mb-4"
          >
            Nenhum aluno com métricas encontrado para a turma selecionada.
          </v-alert>
          <div class="student-selection-scroll">
            <v-chip-group
              v-model="selectedStudentIndex"
              direction="vertical"
              class="student-chip-group"
            >
              <v-chip
                v-for="(student, index) in filteredStudentList"
                :key="student.id"
                :value="index"
                variant="tonal"
                class="student-chip"
                :class="{ 'selected-chip': selectedStudentIndex === index }"
              >
                {{ student.name }}
              </v-chip>
            </v-chip-group>
          </div>
        </v-card-text>
      </v-card>

      <v-card elevation="2">
        <v-toolbar color="primary" elevation="1">
          <v-spacer />Métricas por Período<v-spacer />
        </v-toolbar>
        <v-card-text>
          <v-alert v-if="!selectedStudent" type="info" variant="tonal">
            Selecione um aluno acima para ver o gráfico individual.
          </v-alert>
          <div v-else>
            <p>
              <strong>Aluno selecionado:</strong> {{ selectedStudent.name }}
            </p>
            <v-row v-if="selectedStudentMedRecords.length" class="mb-4">
              <v-col cols="12" md="8">
                <v-card elevation="2" height="100%">
                  <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                    Evolução da Média por Período
                  </v-card-title>
                  <v-card-text>
                    <div style="height: 220px; position: relative;">
                      <Line
                        :data="periodEvolutionChartData"
                        :options="periodEvolutionChartOptions"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card elevation="2" height="100%">
                  <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                    Distribuição das Notas
                  </v-card-title>
                  <v-card-text>
                    <div style="height: 220px; position: relative;">
                      <Doughnut
                        :data="gradeDistributionData"
                        :options="gradeDistributionOptions"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row v-if="selectedStudentTpRecords.length" class="mb-4">
              <v-col cols="12" md="8">
                <v-card elevation="2" height="100%">
                  <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                    Evolução do Teste de Progresso por Período
                  </v-card-title>
                  <v-card-text>
                    <div style="height: 220px; position: relative;">
                      <Line
                        :data="tpEvolutionChartData"
                        :options="tpEvolutionChartOptions"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card elevation="2" height="100%">
                  <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                    Distribuição das Notas de TP
                  </v-card-title>
                  <v-card-text>
                    <div style="height: 220px; position: relative;">
                      <Doughnut
                        :data="tpGradeDistributionData"
                        :options="gradeDistributionOptions"
                      />
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
            <v-alert
              v-if="!selectedStudentMedRecords.length && !selectedStudentTpRecords.length"
              type="info"
              variant="tonal"
            >
              Nenhum dado numérico está disponível para o aluno selecionado.
            </v-alert>

          </div>
        </v-card-text>
      </v-card>

      <v-card elevation="2" class="mt-4">
        <v-toolbar color="secondary" elevation="1">
          <v-spacer />Notas por disciplina no período selecionado<v-spacer />
        </v-toolbar>
        <v-card-text>
          <v-alert v-if="!selectedStudent" type="info" variant="tonal">
            Selecione um aluno acima para ver as notas de disciplina.
          </v-alert>
          <div v-else-if="!studentDisciplineRows.length">
            <v-alert type="info" variant="tonal">
              Nenhuma nota de disciplina disponível para este aluno.
            </v-alert>
          </div>
          <div v-else>
            <v-row class="mb-4" align="center">
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedDisciplinePeriod"
                  :items="availableDisciplinePeriods"
                  label="Período"
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <v-row align="stretch">
              <v-col cols="12" md="7">
                <div class="chart-container">
                  <Bar
                    :data="disciplineChartData"
                    :options="disciplineChartOptions"
                  />
                </div>
              </v-col>
              <v-col cols="12" md="5">
                <div class="chart-container">
                  <Radar
                    :data="disciplineRadarChartData"
                    :options="disciplineRadarChartOptions"
                  />
                </div>
              </v-col>
            </v-row>

            <div v-if="teoAlerts.length" class="mb-4 mt-3 d-flex flex-wrap ga-2">
              <v-tooltip
                v-for="alert in teoAlerts"
                :key="alert.id"
                location="top"
              >
                <template #activator="{ props }">
                  <v-chip
                    v-bind="props"
                    :color="alert.severity === 'critical' ? 'error' : 'warning'"
                    :variant="alert.severity === 'critical' ? 'elevated' : 'tonal'"
                    :class="{ 'teo-alert-critical': alert.severity === 'critical' }"
                    size="small"
                    :prepend-icon="alert.severity === 'critical' ? 'mdi-alert-octagon' : 'mdi-alert'"
                  >
                    {{ alert.discipline }} · {{ alert.label }}: {{ alert.percent }}%
                  </v-chip>
                </template>
                <span>
                  {{ formatValue(alert.value) }} de {{ formatValue(alert.max) }}
                  ({{ alert.percent }}%)
                  <template v-if="alert.severity === 'critical'">
                    — CRÍTICO: abaixo de 45%
                  </template>
                  <template v-else> — abaixo de 60%</template>
                </span>
              </v-tooltip>
            </div>

            <div v-if="availableBreakdownDisciplines.length" class="mt-4">
              <v-row align="center" class="mb-3">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="selectedBreakdownDiscipline"
                    :items="availableBreakdownDisciplines"
                    label="Disciplina para composição"
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="12" md="8">
                  <div class="chart-container">
                    <Bar
                      :data="breakdownChartData"
                      :options="breakdownChartOptions"
                    />
                  </div>
                </v-col>
                <v-col cols="12" md="4" v-if="disciplineBreakdownGauges">
                  <!-- A1(+A3) e A2(+A3) lado a lado -->
                  <v-row no-gutters>
                    <v-col cols="6" class="text-center px-1 mb-1">
                      <div style="height: 110px; position: relative;">
                        <Doughnut
                          :data="disciplineBreakdownGauges.a1.data"
                          :options="disciplineBreakdownGauges.a1.options"
                          :plugins="[centerTextPlugin]"
                        />
                      </div>
                      <div class="text-caption gauge-label">{{ disciplineBreakdownGauges.a1.label }}</div>
                    </v-col>
                    <v-col cols="6" class="text-center px-1 mb-1">
                      <div style="height: 110px; position: relative;">
                        <Doughnut
                          :data="disciplineBreakdownGauges.a2.data"
                          :options="disciplineBreakdownGauges.a2.options"
                          :plugins="[centerTextPlugin]"
                        />
                      </div>
                      <div class="text-caption gauge-label">{{ disciplineBreakdownGauges.a2.label }}</div>
                    </v-col>
                  </v-row>
                  <!-- Média Final centralizada -->
                  <v-row no-gutters justify="center">
                    <v-col cols="6" class="text-center px-1 mb-1">
                      <div style="height: 110px; position: relative;">
                        <Doughnut
                          :data="disciplineBreakdownGauges.average.data"
                          :options="disciplineBreakdownGauges.average.options"
                          :plugins="[centerTextPlugin]"
                        />
                      </div>
                      <div class="text-caption gauge-label">{{ disciplineBreakdownGauges.average.label }}</div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <div v-if="selectedDisciplineRecord" class="mt-3">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-chip variant="tonal" color="warning">
                      Faltas A1: {{ selectedDisciplineRecord.a1Absences ?? 0 }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-chip variant="tonal" color="warning">
                      Faltas A2: {{ selectedDisciplineRecord.a2Absences ?? 0 }}
                    </v-chip>
                  </v-col>
                </v-row>
              </div>

              <div
                v-if="selectedDisciplineTeoMessages.length"
                class="mt-3 d-flex flex-wrap ga-2"
              >
                <v-tooltip
                  v-for="msg in selectedDisciplineTeoMessages"
                  :key="msg.id"
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-chip
                      v-bind="props"
                      :color="msg.severity === 'critical' ? 'error' : msg.severity === 'warning' ? 'warning' : 'success'"
                      :variant="msg.severity === 'critical' ? 'elevated' : 'tonal'"
                      :class="{ 'teo-alert-critical': msg.severity === 'critical' }"
                      size="small"
                      :prepend-icon="msg.severity === 'critical' ? 'mdi-alert-octagon' : msg.severity === 'warning' ? 'mdi-alert' : 'mdi-check-circle'"
                    >
                      {{ msg.label }}: {{ msg.percent }}%
                    </v-chip>
                  </template>
                  <span>
                    {{ formatValue(msg.value) }} de {{ formatValue(msg.max) }}
                    ({{ msg.percent }}%)
                    <template v-if="msg.severity === 'critical'">
                      — CRÍTICO: abaixo de 45%
                    </template>
                    <template v-else-if="msg.severity === 'warning'"> — abaixo de 60%</template>
                    <template v-else> — dentro do esperado (≥ 60%)</template>
                  </span>
                </v-tooltip>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { Bar, Doughnut, Line, Radar } from "vue-chartjs";
import UserDataService from "@/assets/js/services/UserDataService";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend
);

const centerTextPlugin = {
  id: "centerTextIndividual",
  afterDraw(chart) {
    const opts = chart.options.centerText;
    if (!opts) return;
    const { ctx, chartArea } = chart;
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (opts.dual) {
      ctx.font = "bold 9px sans-serif";
      ctx.fillStyle = "#888";
      ctx.fillText(opts.period, cx, cy - 17);
      ctx.font = "bold 11px sans-serif";
      ctx.fillStyle = opts.medColor || "#1565c0";
      ctx.fillText(opts.medValue, cx, cy - 4);
      ctx.font = "bold 11px sans-serif";
      ctx.fillStyle = opts.tpColor || "#6E3C8F";
      ctx.fillText(opts.tpValue, cx, cy + 10);
    } else {
      ctx.font = "bold 14px sans-serif";
      ctx.fillStyle = "#333333";
      ctx.fillText(opts.value, cx, cy - 8);
      ctx.font = "9px sans-serif";
      ctx.fillStyle = "#777777";
      ctx.fillText(opts.label, cx, cy + 8);
    }
    ctx.restore();
  },
};

const overlayA3Plugin = {
  id: "overlayA3",
  afterDatasetsDraw(chart) {
    const opts = chart.options.plugins?.overlayA3;
    if (!opts) return;
    const { a3a1Data = [], a3a2Data = [] } = opts;
    const { ctx, scales } = chart;
    const yScale = scales.y;

    chart.data.datasets.forEach((dataset, dsIdx) => {
      const isA1 = dataset.label === "A1";
      const isA2 = dataset.label === "A2";
      if (!isA1 && !isA2) return;
      const overlay = isA1 ? a3a1Data : a3a2Data;
      const color = isA1 ? "#E65100" : "#C2185B";
      const meta = chart.getDatasetMeta(dsIdx);
      overlay.forEach((val, i) => {
        if (val === null || val === undefined) return;
        const bar = meta.data[i];
        if (!bar) return;
        const w = bar.width * 0.58;
        const x = bar.x - w / 2;
        const yTop = yScale.getPixelForValue(val);
        const yBottom = yScale.getPixelForValue(0);
        ctx.save();
        ctx.fillStyle = color;
        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, yTop, w, yBottom - yTop, 4);
        } else {
          ctx.rect(x, yTop, w, yBottom - yTop);
        }
        ctx.fill();
        ctx.restore();
      });
    });
  },
};

export default {
  name: "TurmaIndividual",
  components: { Bar, Doughnut, Line, Radar },
  props: {
    students: {
      type: Array,
      default: () => [],
    },
    disciplineRecords: {
      type: Array,
      default: () => [],
    },
    simulatedMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["turma-selecionada"],
  data: () => ({
    centerTextPlugin,
    turmas: [],
    turmaAlunos: [],
    selectedTurmaId: null,
    loadingTurmas: false,
    loadingAlunos: false,
    selectedStudentIndex: null,
    selectedDisciplinePeriod: "",
    selectedBreakdownDiscipline: "",
    periods: ["1P", "2P", "3P", "4P", "5P", "6P", "7P", "8P", "9P", "10P", "11P", "12P"],
  }),
  computed: {
    studentList() {
      return this.students;
    },
    filteredStudentList() {
      if (this.simulatedMode || !this.selectedTurmaId) {
        return this.studentList;
      }
      if (!this.turmaAlunos.length) {
        // Turma selecionada mas sem alunos matriculados em TurmaAluno: o
        // resultado correto e "sem dados", nunca cair para "todos os alunos".
        return [];
      }

      const studentsByName = new Map(
        this.studentList.map((student) => [
          this.normalizeStudentName(student.name),
          student,
        ])
      );
      const studentsByRa = new Map(
        this.studentList
          .filter((student) => student.ra)
          .map((student) => [String(student.ra).trim(), student])
      );
      return this.turmaAlunos.map((aluno) => {
        const name = aluno.nome || aluno.email || `Aluno ${aluno.aluno_id || aluno.id}`;
        const ra = String(aluno.ra || "").trim();
        const matchedStudent =
          (ra && studentsByRa.get(ra)) ||
          studentsByName.get(this.normalizeStudentName(name));
        return matchedStudent || {
          id: aluno.aluno_id || aluno.id,
          ra,
          name,
          medSeries: [],
          tpSeries: [],
          values: [],
          seriesLabels: [],
        };
      });
    },
    selectedStudent() {
      return this.filteredStudentList[this.selectedStudentIndex] || null;
    },
    selectedStudentPerformanceRecords() {
      const student = this.selectedStudent;
      if (!student) return [];

      if (student.seriesLabels?.length && student.values?.length) {
        return student.seriesLabels
          .map((label, index) => {
            const parts = String(label || "").split("_");
            return {
              series: /^tp$/i.test(parts[0]) ? "TP" : "MED",
              period: parts[1] || label,
              value: student.values[index],
            };
          })
          .filter((record) => record.value !== null && record.value !== undefined);
      }

      const records = [];
      (student.medSeries || []).forEach((value, index) => {
        if (value !== null && value !== undefined) {
          records.push({
            series: "MED",
            period: this.periods[index] || `${index + 1}P`,
            value,
          });
        }
      });
      (student.tpSeries || []).forEach((value, index) => {
        if (value !== null && value !== undefined) {
          records.push({
            series: "TP",
            period: this.periods[index] || `${index + 1}P`,
            value,
          });
        }
      });
      return records;
    },
    selectedStudentMedRecords() {
      return this.selectedStudentPerformanceRecords
        .filter((record) => record.series === "MED")
        .sort((a, b) => this.periodSortValue(a.period) - this.periodSortValue(b.period));
    },
    selectedStudentTpRecords() {
      return this.selectedStudentPerformanceRecords
        .filter((record) => record.series === "TP")
        .sort((a, b) => this.periodSortValue(a.period) - this.periodSortValue(b.period));
    },
    chartLabels() {
      const student = this.selectedStudent;
      const medData = student?.medSeries || [];
      const tpData = student?.tpSeries || [];

      if (medData.length || tpData.length) {
        const length = Math.max(medData.length, tpData.length);
        return this.periods.slice(0, length);
      }

      return this.selectedStudent?.seriesLabels?.length
        ? this.selectedStudent.seriesLabels
        : this.periods;
    },
    chartDatasets() {
      const student = this.selectedStudent;
      if (!student) return [];

      const medData = student.medSeries || [];
      const tpData = student.tpSeries || [];
      const datasets = [];

      if (medData.length || tpData.length) {
        if (medData.length) {
          datasets.push({
            label: "Média semestral",
            backgroundColor: "#3C6E8F",
            borderRadius: 10,
            data: medData,
          });
        }
        if (tpData.length) {
          datasets.push({
            label: "Média TP",
            backgroundColor: "#6E3C8F",
            borderRadius: 10,
            data: tpData,
          });
        }
        return datasets;
      }

      const values = student.values || [];
      const labels = student.seriesLabels || [];

      if (values.length && labels.length === values.length) {
        const backgroundColor = labels.map((label) => {
          const normalized = String(label || "").toLowerCase();
          return /tp/.test(normalized) ? "#6E3C8F" : "#3C6E8F";
        });

        return [
          {
            label: "Desempenho do aluno",
            backgroundColor,
            borderRadius: 10,
            data: values,
          },
        ];
      }

      return [];
    },
    chartDataComparison() {
      return {
        labels: this.chartLabels,
        datasets: this.chartDatasets,
      };
    },
    chartOptionsComparison() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 1 },
          },
          x: {
            grid: { display: false },
          },
        },
      };
    },
    periodEvolutionChartData() {
      return this.buildLineChartData(
        this.selectedStudentMedRecords,
        "Média por Período",
        "#1565c0",
        "rgba(21, 101, 192, 0.08)"
      );
    },
    periodEvolutionChartOptions() {
      return this.buildLineChartOptions("Média");
    },
    gradeDistributionData() {
      return this.buildDistributionData(
        this.selectedStudentMedRecords.map((record) => record.value)
      );
    },
    tpEvolutionChartData() {
      return this.buildLineChartData(
        this.selectedStudentTpRecords,
        "Teste de Progresso",
        "#2e7d32",
        "rgba(46, 125, 50, 0.08)"
      );
    },
    tpEvolutionChartOptions() {
      return this.buildLineChartOptions("TP");
    },
    tpGradeDistributionData() {
      return this.buildDistributionData(
        this.selectedStudentTpRecords.map((record) => record.value)
      );
    },
    gradeDistributionOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "65%",
        plugins: {
          legend: {
            position: "bottom",
            labels: { boxWidth: 10, font: { size: 11 }, padding: 8 },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((sum, value) => sum + value, 0);
                const pct = total ? Math.round((ctx.raw / total) * 100) : 0;
                return `${ctx.label}: ${ctx.raw} (${pct}%)`;
              },
            },
          },
        },
      };
    },
    periodGauges() {
      const student = this.selectedStudent;
      if (!student) return [];
      const medData = student.medSeries || [];
      const tpData = student.tpSeries || [];
      return this.chartLabels.map((period, i) =>
        this.buildDualGaugeConfig(medData[i] ?? null, tpData[i] ?? null, period)
      );
    },
    disciplineBreakdownGauges() {
      const rec = this.selectedDisciplineRecord;
      if (!rec) return null;
      const a1Gauge = rec.a3a1 != null
        ? this.buildDualGaugeConfig(rec.a1, rec.a3a1, "A1", "A1", "A3 de A1")
        : this.buildGaugeConfig(rec.a1, "A1", "Nota A1");
      const a2Gauge = rec.a3a2 != null
        ? this.buildDualGaugeConfig(rec.a2, rec.a3a2, "A2", "A2", "A3 de A2")
        : this.buildGaugeConfig(rec.a2, "A2", "Nota A2");
      return {
        a1: { ...a1Gauge, label: rec.a3a1 != null ? "A1 / A3 de A1" : "Nota A1" },
        a2: { ...a2Gauge, label: rec.a3a2 != null ? "A2 / A3 de A2" : "Nota A2" },
        average: this.buildGaugeConfig(rec.average, "MÉDIA", "Média Final"),
      };
    },
    availableDisciplinePeriods() {
      const studentName = this.normalizeStudentName(this.selectedStudent?.name);
      const studentRa = String(this.selectedStudent?.ra || "").trim();
      const periods = this.disciplineRecords
        .filter(
          (record) =>
            (studentRa && String(record.ra || "").trim() === studentRa) ||
            this.normalizeStudentName(record.studentName) === studentName
        )
        .map((record) => record.period)
        .filter(Boolean);
      return [...new Set(periods)];
    },
    availableBreakdownDisciplines() {
      return [...new Set(this.studentDisciplineRows.map((row) => row.discipline))];
    },
    selectedDisciplineRecord() {
      return (
        this.studentDisciplineRows.find(
          (row) => row.discipline === this.selectedBreakdownDiscipline
        ) || this.studentDisciplineRows[0] || null
      );
    },
    studentDisciplineRows() {
      const studentName = this.normalizeStudentName(this.selectedStudent?.name);
      const studentRa = String(this.selectedStudent?.ra || "").trim();
      if (!studentName || !this.selectedDisciplinePeriod) return [];
      return this.disciplineRecords.filter(
        (record) =>
          ((studentRa && String(record.ra || "").trim() === studentRa) ||
            this.normalizeStudentName(record.studentName) === studentName) &&
          record.period === this.selectedDisciplinePeriod
      );
    },
    teoAlerts() {
      const alerts = [];

      this.studentDisciplineRows.forEach((row) => {
        this.disciplineGradeChecks(row).forEach(({ id, label, value, max }) => {
          const alert = this.buildGradeAlert(value, max);
          if (!alert) return;
          alerts.push({ id: `${row.discipline}-${id}`, discipline: row.discipline, label, ...alert });
        });
      });

      return alerts.sort((a, b) => {
        if (a.severity !== b.severity) return a.severity === "critical" ? -1 : 1;
        return a.discipline.localeCompare(b.discipline, "pt-BR");
      });
    },
    selectedDisciplineTeoMessages() {
      const record = this.selectedDisciplineRecord;
      if (!record) return [];

      return this.disciplineGradeChecks(record).map(({ id, label, value, max }) => ({
        id,
        label,
        ...this.buildGradeMessage(value, max),
      }));
    },
    disciplineRadarChartData() {
      const rows = this.studentDisciplineRows.filter(
        (row) =>
          !(
            this.normalizeDisciplineName(row.discipline).includes("programa integrador") &&
            Number(row.average || 0) === 0
          )
      );
      const labels = rows.map((row) => row.discipline);
      const studentData = rows.map((row) =>
        row.average !== null && row.average !== undefined ? row.average : 0
      );
      const classData = labels.map((discipline) =>
        this.classAverageByDiscipline(discipline)
      );

      return {
        labels,
        datasets: [
          {
            label: "Aluno",
            data: studentData,
            borderColor: "#1565c0",
            backgroundColor: "rgba(21, 101, 192, 0.18)",
            pointBackgroundColor: "#1565c0",
            pointBorderColor: "#ffffff",
            pointRadius: 4,
            borderWidth: 2,
          },
          {
            label: "Turma",
            data: classData,
            borderColor: "#fbc02d",
            backgroundColor: "rgba(251, 192, 45, 0.22)",
            pointBackgroundColor: "#fbc02d",
            pointBorderColor: "#ffffff",
            pointRadius: 4,
            borderWidth: 2,
          },
        ],
      };
    },
    disciplineRadarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: "top" },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: ${this.formatValue(ctx.raw)}`,
            },
          },
        },
        scales: {
          r: {
            beginAtZero: true,
            max: 10,
            ticks: {
              stepSize: 2,
              backdropColor: "transparent",
            },
            pointLabels: {
              display: false,
            },
            grid: { color: "rgba(0,0,0,0.08)" },
            angleLines: { color: "rgba(0,0,0,0.08)" },
          },
        },
      };
    },
    disciplineChartData() {
      const rows = this.studentDisciplineRows;
      const labels = rows.map((row) => row.discipline);
      const a1Data = rows.map((row) => (row.a1 !== null ? row.a1 : null));
      const a2Data = rows.map((row) => (row.a2 !== null ? row.a2 : null));
      const a3a1Data = rows.map((row) =>
        row.a3a1 !== null && row.a3a1 !== undefined ? row.a3a1 : null
      );
      const a3a2Data = rows.map((row) =>
        row.a3a2 !== null && row.a3a2 !== undefined ? row.a3a2 : null
      );

      const hasAnyA3a1 = rows.some((row) => row.a3a1 !== null && row.a3a1 !== undefined);
      const hasAnyA3a2 = rows.some((row) => row.a3a2 !== null && row.a3a2 !== undefined);

      // Barras base ficam sombreadas (transparentes) onde há recuperação por cima
      const a1Colors = rows.map((row) =>
        row.a3a1 !== null && row.a3a1 !== undefined
          ? "rgba(60,110,143,0.35)"
          : "#3C6E8F"
      );
      const a2Colors = rows.map((row) =>
        row.a3a2 !== null && row.a3a2 !== undefined
          ? "rgba(110,60,143,0.35)"
          : "#6E3C8F"
      );

      const datasets = [];
      if (rows.some((row) => row.a1 !== null)) {
        datasets.push({
          label: "A1",
          backgroundColor: hasAnyA3a1 ? a1Colors : "#3C6E8F",
          borderRadius: 4,
          data: a1Data,
          order: 2,
          barPercentage: 0.9,
          categoryPercentage: 0.8,
        });
      }
      if (hasAnyA3a1) {
        datasets.push({
          label: "A3 de A1",
          backgroundColor: "#E65100",
          borderRadius: 4,
          data: a3a1Data,
          order: 1,
          barPercentage: 0.55,
          categoryPercentage: 0.8,
        });
      }
      if (rows.some((row) => row.a2 !== null)) {
        datasets.push({
          label: "A2",
          backgroundColor: hasAnyA3a2 ? a2Colors : "#6E3C8F",
          borderRadius: 4,
          data: a2Data,
          order: 2,
          barPercentage: 0.9,
          categoryPercentage: 0.8,
        });
      }
      if (hasAnyA3a2) {
        datasets.push({
          label: "A3 de A2",
          backgroundColor: "#C2185B",
          borderRadius: 4,
          data: a3a2Data,
          order: 1,
          barPercentage: 0.55,
          categoryPercentage: 0.8,
        });
      }

      return { labels, datasets };
    },
    disciplineChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: "top" },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y ?? ctx.raw;
                return `${ctx.dataset.label}: ${val}`;
              },
            },
          },
        },
        scales: {
          y: { beginAtZero: true, max: 10, ticks: { stepSize: 1 } },
          x: {
            grid: { display: false },
            grouped: false,
          },
        },
      };
    },
    breakdownChartData() {
      const record = this.selectedDisciplineRecord;
      if (!record) return { labels: [], datasets: [] };

      const components = [];

      if (record.componentsA1 && record.componentsA1.length > 0) {
        components.push(...record.componentsA1.map((c) => ({ ...c, side: "a1" })));
      } else if (record.a1 !== null && record.a1 !== undefined) {
        components.push({ label: "A1", value: record.a1, side: "a1" });
      }

      if (record.a3a1 !== null && record.a3a1 !== undefined) {
        components.push({ label: "A3 de A1", value: record.a3a1, isRecovery: true, side: "a1" });
      }

      if (record.componentsA2 && record.componentsA2.length > 0) {
        components.push(...record.componentsA2.map((c) => ({ ...c, side: "a2" })));
      } else if (record.a2 !== null && record.a2 !== undefined) {
        components.push({ label: "A2", value: record.a2, side: "a2" });
      }

      if (record.a3a2 !== null && record.a3a2 !== undefined) {
        components.push({ label: "A3 de A2", value: record.a3a2, isRecovery: true, side: "a2" });
      }

      const labels = components.map((item) => item.label);
      const data = components.map((item) => item.value);
      const backgroundColor = components.map((item) => {
        if (item.isRecovery && item.side === "a2") return "#C2185B";
        if (item.isRecovery) return "#E65100";
        if (item.side === "a2") return "#6E3C8F";
        return "#3C6E8F";
      });

      return {
        labels,
        datasets: [{
          label: "Composição de A1/A2",
          backgroundColor,
          borderRadius: 10,
          data,
        }],
      };
    },
    breakdownChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          title: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `${context.dataset.label}: ${context.parsed.y}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 1 },
          },
          x: {
            grid: { display: false },
          },
        },
      };
    },
  },
  watch: {
    selectedTurmaId(value) {
      this.selectedStudentIndex = null;
      this.selectedDisciplinePeriod = "";
      this.selectedBreakdownDiscipline = "";
      this.loadTurmaAlunos(value);
      const turma = this.turmas.find((item) => item.id === value);
      this.$emit("turma-selecionada", turma?.codigo || null);
    },
    filteredStudentList(newStudents) {
      if (!newStudents.length) {
        this.selectedStudentIndex = null;
        return;
      }
      if (
        this.selectedStudentIndex === null ||
        this.selectedStudentIndex >= newStudents.length
      ) {
        this.selectedStudentIndex = 0;
      }
    },
    students(newStudents) {
      if (
        newStudents.length &&
        this.selectedStudentIndex >= this.filteredStudentList.length
      ) {
        this.selectedStudentIndex = 0;
      }
    },
    selectedStudent() {
      if (!this.selectedStudent) {
        this.selectedDisciplinePeriod = "";
        this.selectedBreakdownDiscipline = "";
        return;
      }
      const periods = this.availableDisciplinePeriods;
      if (periods.length && !periods.includes(this.selectedDisciplinePeriod)) {
        this.selectedDisciplinePeriod = periods[0];
      }
    },
    availableDisciplinePeriods(newPeriods) {
      if (
        newPeriods.length &&
        !newPeriods.includes(this.selectedDisciplinePeriod)
      ) {
        this.selectedDisciplinePeriod = newPeriods[0];
      }
      if (!newPeriods.length) {
        this.selectedBreakdownDiscipline = "";
      }
    },
    selectedDisciplinePeriod(newPeriod) {
      if (
        newPeriod &&
        this.availableBreakdownDisciplines.length &&
        !this.availableBreakdownDisciplines.includes(
          this.selectedBreakdownDiscipline
        )
      ) {
        this.selectedBreakdownDiscipline = this.availableBreakdownDisciplines[0];
      }
    },
    studentDisciplineRows(newRows) {
      if (
        newRows.length &&
        !newRows.some((row) => row.discipline === this.selectedBreakdownDiscipline)
      ) {
        this.selectedBreakdownDiscipline = newRows[0].discipline;
      }
    },
  },
  mounted() {
    this.loadTurmas();
  },
  methods: {
    buildLineChartData(records, label, borderColor, backgroundColor) {
      return {
        labels: records.map((record) => record.period),
        datasets: [
          {
            label,
            data: records.map((record) => record.value),
            borderColor,
            backgroundColor,
            fill: true,
            tension: 0.35,
            pointBackgroundColor: borderColor,
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      };
    },
    buildLineChartOptions(label) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `${label}: ${this.formatValue(ctx.raw)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 2 },
            grid: { color: "rgba(0,0,0,0.06)" },
          },
          x: { grid: { display: false } },
        },
      };
    },
    buildDistributionData(values) {
      const numericValues = values.filter((value) => value !== null && value !== undefined);
      const ranges = [
        { label: "0,0 - 2,9", min: 0, max: 3, color: "#d32f2f" },
        { label: "3,0 - 5,9", min: 3, max: 6, color: "#fb8c00" },
        { label: "6,0 - 7,9", min: 6, max: 8, color: "#1565c0" },
        { label: "8,0 - 10", min: 8, max: 10.01, color: "#388e3c" },
      ];
      return {
        labels: ranges.map((range) => range.label),
        datasets: [
          {
            data: ranges.map((range) =>
              numericValues.filter((value) => value >= range.min && value < range.max).length
            ),
            backgroundColor: ranges.map((range) => range.color),
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      };
    },
    periodSortValue(period) {
      const match = String(period || "").match(/\d+/);
      return match ? Number(match[0]) : 999;
    },
    async loadTurmas() {
      this.loadingTurmas = true;
      try {
        const response = await UserDataService.listTurmas();
        this.turmas = (response.data || []).map((turma) => ({
          ...turma,
          display: turma.codigo,
        }));

        if (this.turmas.length && !this.selectedTurmaId) {
          this.selectedTurmaId = this.turmas[0].id;
        }
      } catch (_error) {
        this.turmas = [];
      } finally {
        this.loadingTurmas = false;
      }
    },
    async loadTurmaAlunos(turmaId) {
      if (!turmaId) {
        this.turmaAlunos = [];
        return;
      }

      this.loadingAlunos = true;
      try {
        const response = await UserDataService.listTurmaAlunos({ id: turmaId });
        this.turmaAlunos = response.data || [];
      } catch (_error) {
        this.turmaAlunos = [];
      } finally {
        this.loadingAlunos = false;
      }
    },
    performanceColor(value) {
      if (value === null || value === undefined) return "#e0e0e0";
      if (value < 3) return "#d32f2f";
      if (value < 6) return "#fb8c00";
      return "#388e3c";
    },
    buildGaugeConfig(value, centerLabel, fullLabel) {
      const hasValue = value !== null && value !== undefined;
      const color = this.performanceColor(value);
      const filled = hasValue ? Math.min(Math.max(value, 0), 10) : 0;
      const displayValue = hasValue
        ? value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "-";
      return {
        label: fullLabel,
        data: {
          datasets: [{
            data: hasValue ? [filled, 10 - filled] : [10, 0],
            backgroundColor: hasValue ? [color, "#e0e0e0"] : ["#e0e0e0", "#e0e0e0"],
            borderWidth: 0,
            hoverOffset: 0,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          centerText: { value: displayValue, label: centerLabel },
          animation: { duration: 400 },
        },
      };
    },
    buildDualGaugeConfig(medValue, tpValue, period, outerLabel = "Média Sem.", innerLabel = "Média TP") {
      const fmt = (v) =>
        v !== null && v !== undefined
          ? v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 })
          : "-";
      const fill = (v) => v !== null && v !== undefined ? Math.min(Math.max(v, 0), 10) : 0;
      const medColor = this.performanceColor(medValue);
      const tpColor = this.performanceColor(tpValue);
      const medHas = medValue !== null && medValue !== undefined;
      const tpHas = tpValue !== null && tpValue !== undefined;
      return {
        label: period,
        data: {
          datasets: [
            {
              data: medHas ? [fill(medValue), 10 - fill(medValue)] : [10, 0],
              backgroundColor: medHas ? [medColor, "#e8e8e8"] : ["#e0e0e0", "#e0e0e0"],
              borderWidth: 2,
              borderColor: "#fff",
              hoverOffset: 0,
            },
            {
              data: tpHas ? [fill(tpValue), 10 - fill(tpValue)] : [10, 0],
              backgroundColor: tpHas ? [tpColor, "#e8e8e8"] : ["#e0e0e0", "#e0e0e0"],
              borderWidth: 2,
              borderColor: "#fff",
              hoverOffset: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "52%",
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  if (ctx.dataIndex === 1) return null;
                  const name = ctx.datasetIndex === 0 ? outerLabel : innerLabel;
                  const val = ctx.datasetIndex === 0 ? fmt(medValue) : fmt(tpValue);
                  return `${name}: ${val}`;
                },
              },
            },
          },
          centerText: {
            dual: true,
            period,
            medValue: fmt(medValue),
            tpValue: fmt(tpValue),
            medColor,
            tpColor,
          },
          animation: { duration: 400 },
        },
      };
    },
    selectStudent(index) {
      this.selectedStudentIndex = index;
    },
    formatValue(value) {
      return value.toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      });
    },
    normalizeStudentName(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    },
    normalizeDisciplineName(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim()
        .toLowerCase();
    },
    teoComponentInfo(components, rawTotal) {
      if (Array.isArray(components) && components.length) {
        const teo = components.find((component) =>
          /teo/i.test(component.label)
        );
        // Cada fator secund\u00e1rio presente reduz o m\u00e1ximo da te\u00f3rica: pr\u00e1tica
        // ou PBL tiram 2 pontos cada; portf\u00f3lio (PORT) tira 1 ponto.
        const secondaryDeduction = components.reduce((total, component) => {
          if (/prat/i.test(component.label) || /pbl/i.test(component.label)) {
            return total + 2;
          }
          if (/port/i.test(component.label)) {
            return total + 1;
          }
          return total;
        }, 0);
        const max = Math.max(10 - secondaryDeduction, 0);

        if (teo) return { value: teo.value, max };
        // S\u00f3 um fator lan\u00e7ado: por defini\u00e7\u00e3o sempre existe a te\u00f3rica, ent\u00e3o
        // esse fator \u00fanico \u00e9 a nota te\u00f3rica (m\u00e1ximo 10).
        if (components.length === 1) return { value: components[0].value, max: 10 };
        return null;
      }
      // Sem detalhamento por fator dispon\u00edvel: usa a nota do bimestre como
      // melhor estimativa da te\u00f3rica (composi\u00e7\u00e3o desconhecida, assume m\u00e1ximo 10).
      if (rawTotal === null || rawTotal === undefined) return null;
      return { value: rawTotal, max: 10 };
    },
    disciplineGradeChecks(row) {
      const checks = [];

      const teoA1 = this.teoComponentInfo(row.componentsA1, row.a1);
      if (teoA1) {
        checks.push({ id: "teo-a1", label: "Nota teórica (A1)", value: teoA1.value, max: teoA1.max });
      }
      const teoA2 = this.teoComponentInfo(row.componentsA2, row.a2);
      if (teoA2) {
        checks.push({ id: "teo-a2", label: "Nota teórica (A2)", value: teoA2.value, max: teoA2.max });
      }

      // Avaliações A3 são sempre lançadas de 0 a 10, independente da
      // composição da disciplina.
      if (row.a3a1 !== null && row.a3a1 !== undefined) {
        checks.push({ id: "a3-a1", label: "A3 de A1", value: row.a3a1, max: 10 });
      }
      if (row.a3a2 !== null && row.a3a2 !== undefined) {
        checks.push({ id: "a3-a2", label: "A3 de A2", value: row.a3a2, max: 10 });
      }

      return checks;
    },
    buildGradeAlert(value, max) {
      if (value === null || value === undefined || !max) return null;
      const warningThreshold = max * 0.6;
      const criticalThreshold = max * 0.45;
      if (value >= warningThreshold) return null;
      return {
        value,
        max,
        percent: Math.round((value / max) * 100),
        severity: value < criticalThreshold ? "critical" : "warning",
      };
    },
    buildGradeMessage(value, max) {
      const percent = Math.round((value / max) * 100);
      let severity = "ok";
      if (value < max * 0.45) severity = "critical";
      else if (value < max * 0.6) severity = "warning";
      return { value, max, percent, severity };
    },
    classAverageByDiscipline(discipline) {
      if (!this.selectedDisciplinePeriod) return null;
      const values = this.disciplineRecords
        .filter(
          (record) =>
            record.period === this.selectedDisciplinePeriod &&
            record.discipline === discipline
        )
        .map((record) => record.average)
        .filter((value) => value !== null && value !== undefined);
      return values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
    },
  },
};
</script>

<style scoped>
.student-selection-scroll {
  max-height: 320px;
  padding-right: 8px;
  overflow-y: auto;
}

.student-chip-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.student-chip {
  width: 100%;
  min-width: auto;
  max-width: none;
  white-space: normal;
  text-align: left;
}

.selected-chip {
  background-color: rgba(60, 110, 143, 0.16) !important;
}

.chart-container {
  min-height: 320px;
}

.gauge-label {
  font-size: 10px;
  color: #555;
  word-break: break-word;
}

.teo-alert-critical {
  border: 2px solid #b71c1c;
  animation: teo-alert-pulse 1.6s ease-in-out infinite;
}

@keyframes teo-alert-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(183, 28, 28, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(183, 28, 28, 0);
  }
}
</style>
